import { typo } from '@design-system/styles'
import styled from '@emotion/styled'
import { zIndex } from '@mathflat/common/style'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import qs from 'qs'
import { useEffect, useRef, useState } from 'react'
import { generatePath, useHistory } from 'react-router'

import { Badge } from '@/component/atoms/Badge'
import { Icon } from '@/component/atoms/Icon'
import useRepository from '@/hooks/useRepository'
import { podMallCartService } from '@/page/PODMall/pages/PODMallCart/_service/PODMallCart.service'
import PODMallCouponService from '@/page/PODMall/pages/PODMallManage/PODMallCoupon/_service/PODMallCoupon.service'
import { RouteName } from '@/router'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import { Portal } from '../Portal'

const HeaderMenuPopover = () => {
  const history = useHistory()
  const podManageService = useRepository(PODMallCouponService)

  const ref = useRef<HTMLButtonElement>(null)
  const rect = ref.current?.getBoundingClientRect()

  const [navigationOpened, setNavigationOpened] = useState(false)

  useEffect(() => {
    const handleNavigationOpened = () => {
      setNavigationOpened(false)
    }
    document.addEventListener('click', handleNavigationOpened)

    return () => {
      document.removeEventListener('click', handleNavigationOpened)
    }
  }, [])

  const totalPopoverCount =
    (podMallCartService.cartItemColl?.toArr.length || 0) + podManageService.couponList.length

  const totalPopoverCountStr =
    totalPopoverCount > 99 ? '99' : totalPopoverCount === 0 ? '' : `${totalPopoverCount}`

  return (
    <>
      <button
        className="flex handler-button"
        onClick={(e) => {
          e.stopPropagation()
          mixpanelClient.track('GNB_북메뉴_click')
          setNavigationOpened(!navigationOpened)
        }}
        ref={ref}
      >
        <div className={clsx('icon__wrapper', navigationOpened && 'active')}>
          <Icon name={navigationOpened ? 'memo_active' : 'memo_default'} size={20} />
          {totalPopoverCountStr !== '' && (
            <div className="count-badge noti-badge">{totalPopoverCountStr}</div>
          )}
        </div>

        <Portal.Element isVisible={navigationOpened} rect={rect} offset={{ x: 40, y: 0 }}>
          <HeaderMenuPopoverNav onClick={(e) => e.stopPropagation()}>
            <div className="nav-header">우리 기관 교재 주문 관리</div>
            <ul className="nav-list">
              <li
                className="nav-item"
                onClick={() =>
                  history.push({
                    pathname: RouteName.podMallCart,
                    search: qs.stringify({
                      onClosePath: history.location.pathname,
                    }),
                  })
                }
              >
                <div className="nav-item-title">
                  <p>장바구니</p>
                  {podMallCartService.notiedCartItemCountStr && (
                    <div className="count-badge">{podMallCartService.notiedCartItemCountStr}</div>
                  )}
                </div>
              </li>
              <li className="nav-item" onClick={() => history.push(RouteName.podMallPurchase)}>
                <div className="nav-item-title">주문내역</div>
              </li>
              <li className="nav-item" onClick={() => history.push(RouteName.podMallUser)}>
                <div className="nav-item-title">배송정보 변경</div>
              </li>
              <li className="nav-item" onClick={() => history.push(RouteName.podMallCoupon)}>
                <div className="nav-item-title">
                  <p>할인 쿠폰</p>
                  {podManageService.couponCountStr && (
                    <div className="count-badge">{podManageService.couponCountStr}</div>
                  )}
                </div>
              </li>
            </ul>
            <ul className="nav-list">
              <li
                className="nav-item"
                onClick={() => {
                  _mixpanelUtils.uiTrack('커스텀프린팅_GNB')
                  history.push({
                    pathname: generatePath(RouteName.customPrint),
                    search: qs.stringify({
                      entryPath: history.location.pathname,
                    }),
                  })
                }}
              >
                <div className="description">선생님만의 수업자료를 책으로 만드는</div>
                <strong className="nav-item-title">
                  커스텀 프린팅
                  <Badge
                    backgroundColor="var(--Colors-Orange-500)"
                    color="#fff"
                    size="xs"
                    radius="100"
                  >
                    NEW
                  </Badge>
                </strong>
              </li>
            </ul>
          </HeaderMenuPopoverNav>
        </Portal.Element>
      </button>
    </>
  )
}

export default observer(HeaderMenuPopover)

const HeaderMenuPopoverNav = styled.nav`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 220px;
  top: 44px;
  right: 0;
  background-color: white;
  border: 1px solid var(--Colors-Gray-200);
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.2);
  border-radius: var(--Radius-200);
  z-index: ${zIndex.PopperNavigation};
  overflow: hidden;

  .nav-header {
    ${typo.caption01}
    width: 100%;
    padding: 14px 16px;
    font-weight: 700;
    background-color: var(--Colors-Gray-900);
    color: white;
  }

  .nav-list {
    width: 100%;
    cursor: pointer;

    + .nav-list {
      border-top: 1px solid var(--Colors-Gray-300);
    }
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;

    .description {
      ${typo.caption01};
      color: var(--Colors-Gray-600);
      padding: 14px 16px 0 16px;
    }
  }

  .nav-item-title {
    display: flex;
    align-items: center;
    gap: 4px;
    ${typo.body02};
    padding: 14px 16px;

    &:hover {
      background-color: var(--Colors-Gray-100);
    }

    .count-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      border-radius: var(--Radius-100);
      background: var(--Colors-Blue-500);
      padding: 1px 4px;
      z-index: 10;
      ${typo.caption02};
      font-weight: 700;
    }
  }
`
